import { Link } from 'react-router-dom';
import { Clock, Mail, MapPin, Phone } from 'lucide-react';
import { navLinks } from '@site/data';
import { useCompany, useSettings } from '@site/content';
import { useServices } from '@api/public';

export default function SiteFooter() {
  const company = useCompany();
  const { brand, footer } = useSettings();
  const services = useServices('fire').slice(0, 6);
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-ink-950 text-ink-300">
      <div className="h-1 bg-flame-x" aria-hidden="true" />
      <div className="site-container grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr]">
        <div>
          <Link to="/" className="flex items-center gap-3">
            <img src={brand.logo} alt="" className="h-14 w-14 object-contain" width="56" height="56" loading="lazy" />
            <span className="font-display text-2xl font-extrabold tracking-wide text-white">{company.name}</span>
          </Link>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-ink-400">{footer.about}</p>
          <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-xs text-ink-300">
            Established {company.established} · Bengaluru
          </p>
        </div>

        <div>
          <h2 className="font-display text-lg font-bold tracking-wide text-white">Quick links</h2>
          <ul className="mt-4 space-y-2.5 text-sm">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="hover:text-white">{link.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="font-display text-lg font-bold tracking-wide text-white">Services</h2>
          <ul className="mt-4 space-y-2.5 text-sm">
            {services.map((s) => (
              <li key={s.title}>
                <Link to="/services" className="hover:text-white">{s.title}</Link>
              </li>
            ))}
            <li><Link to="/amc" className="hover:text-white">Annual Maintenance (AMC)</Link></li>
            <li><Link to="/training" className="hover:text-white">Fire & Safety Training</Link></li>
          </ul>
        </div>

        <div>
          <h2 className="font-display text-lg font-bold tracking-wide text-white">Get in touch</h2>
          <ul className="mt-4 space-y-4 text-sm">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent-400" aria-hidden="true" />
              <a href={company.mapLink} target="_blank" rel="noreferrer" className="hover:text-white">{company.address}</a>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-accent-400" aria-hidden="true" />
              <span>
                <a href={company.phoneHref} className="block hover:text-white">{company.phone}</a>
                <a href={company.phone2Href} className="block hover:text-white">{company.phone2}</a>
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-accent-400" aria-hidden="true" />
              <span className="min-w-0 break-words">
                <a href={`mailto:${company.email}`} className="block hover:text-white">{company.email}</a>
                <a href={`mailto:${company.email2}`} className="block hover:text-white">{company.email2}</a>
              </span>
            </li>
            <li className="flex gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-accent-400" aria-hidden="true" />
              <span>{company.days}<br />{company.hours}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="site-container flex flex-col items-start justify-between gap-2 py-5 text-xs text-ink-500 sm:flex-row sm:items-center">
          <p>© {year} {footer.copyright}</p>
          <p>
            <a href="https://www.armedfire.in" className="hover:text-white">www.armedfire.in</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
